import { useCallback, useEffect, useState } from 'react'
import { Button, Card, Form, Modal, Popconfirm, Space, Table, Tag, message } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import { adminApi, formatApiError, type PageResult } from '../../api'
import {
  KNOWLEDGE_SCOPE_OPTIONS_SHORT,
  type KnowledgeScope,
} from '../../constants/knowledgeScope'
import {
  DatasetRuleFields,
  DATASET_MIN_QUALITY_LABEL,
  buildDatasetFilterJson,
  filterJsonToFormValues,
  type DatasetFilterFormValues,
} from '../../components/DatasetRuleFields'
import { PageHeader, tablePagination, useServerPagination, type FolderDimensionOption } from '../../components/ui'

const QUALITY_LABEL = DATASET_MIN_QUALITY_LABEL

const SCOPE_LABEL: Record<string, string> = Object.fromEntries(
  KNOWLEDGE_SCOPE_OPTIONS_SHORT.map((o) => [o.value, o.label]),
)

type DatasetRow = {
  id: number
  dataset_code: string
  name: string
  description: string
  scope: string
  status: string
  filter_json: Record<string, unknown> | null
  is_public: boolean
  item_count: number
}

type DatasetItemRow = {
  id: number
  title: string
  category: string
  index_status: string
  quality_tier?: string
}

const INDEX_STATUS_LABEL: Record<string, string> = {
  indexed: '已索引',
  pending: '待索引',
  failed: '索引失败',
}

const ITEM_QUALITY_LABEL: Record<string, string> = {
  high: '高',
  medium: '中',
  low: '低',
  unknown: '未评',
}

export default function DatasetManage() {
  const [rows, setRows] = useState<DatasetRow[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const { page, pageSize, handlePaginationChange, paginationOptions } = useServerPagination()
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<DatasetRow | null>(null)
  const [saving, setSaving] = useState(false)
  const [deletingId, setDeletingId] = useState<number | null>(null)
  const [form] = Form.useForm<DatasetFilterFormValues>()
  const [dimensionLabels, setDimensionLabels] = useState<Map<number, string>>(new Map())
  const [itemsDataset, setItemsDataset] = useState<DatasetRow | null>(null)
  const [itemRows, setItemRows] = useState<DatasetItemRow[]>([])
  const [itemTotal, setItemTotal] = useState(0)
  const [itemsLoading, setItemsLoading] = useState(false)
  const {
    page: itemPage,
    pageSize: itemPageSize,
    handlePaginationChange: handleItemPaginationChange,
    paginationOptions: itemPaginationOptions,
    resetPage: resetItemPage,
  } = useServerPagination()

  const loadDimensionLabels = useCallback(() => {
    const scopes: KnowledgeScope[] = ['enterprise', 'personal']
    void Promise.all(
      scopes.map((scope) =>
        adminApi.get<{ items: FolderDimensionOption[] }>('/admin/knowledge/folder-dimensions', {
          params: { scope },
        }),
      ),
    ).then((results) => {
      const map = new Map<number, string>()
      for (const r of results) {
        for (const d of r.data.items || []) {
          map.set(d.id, d.is_manual ? '系统目录' : d.name)
        }
      }
      setDimensionLabels(map)
    })
  }, [])

  const load = useCallback(() => {
    setLoading(true)
    adminApi
      .get<PageResult<DatasetRow>>('/admin/datasets', {
        params: { page, page_size: pageSize },
      })
      .then((r) => {
        setRows(r.data.items || [])
        setTotal(r.data.total || 0)
      })
      .catch((e) => message.error(formatApiError(e, '加载失败')))
      .finally(() => setLoading(false))
  }, [page, pageSize])

  useEffect(() => {
    loadDimensionLabels()
  }, [loadDimensionLabels])

  useEffect(() => {
    load()
  }, [load])

  const loadItems = useCallback(() => {
    if (!itemsDataset) return
    setItemsLoading(true)
    adminApi
      .get<PageResult<DatasetItemRow>>(`/admin/datasets/${itemsDataset.id}/items`, {
        params: { page: itemPage, page_size: itemPageSize },
      })
      .then((r) => {
        setItemRows(r.data.items || [])
        setItemTotal(r.data.total || 0)
      })
      .catch((e) => message.error(formatApiError(e, '加载文档失败')))
      .finally(() => setItemsLoading(false))
  }, [itemsDataset, itemPage, itemPageSize])

  useEffect(() => {
    if (itemsDataset) loadItems()
  }, [itemsDataset, loadItems])

  const openItems = (row: DatasetRow) => {
    resetItemPage()
    setItemsDataset(row)
  }

  const closeItems = () => {
    setItemsDataset(null)
    setItemRows([])
    setItemTotal(0)
  }

  const openCreate = () => {
    setEditing(null)
    form.resetFields()
    form.setFieldsValue({
      scope: 'enterprise',
      dimension_id: undefined,
      folder_ids: [],
      min_quality: undefined,
      is_public: false,
    })
    setModalOpen(true)
  }

  const openEdit = (row: DatasetRow) => {
    setEditing(row)
    form.setFieldsValue({
      name: row.name,
      description: row.description,
      scope: row.scope === 'personal' ? 'personal' : 'enterprise',
      is_public: row.is_public,
      status: row.status === 'published' ? 'published' : 'draft',
      ...filterJsonToFormValues(row.filter_json),
    })
    setModalOpen(true)
  }

  const closeModal = () => {
    setModalOpen(false)
    setEditing(null)
    form.resetFields()
  }

  const submit = () => {
    void form.validateFields().then((v) => {
      setSaving(true)
      const body = {
        name: v.name?.trim(),
        description: (v.description || '').trim(),
        scope: v.scope || 'enterprise',
        is_public: !!v.is_public,
        filter_json: buildDatasetFilterJson(v),
      }
      const req = editing
        ? adminApi
            .patch(`/admin/datasets/${editing.id}`, {
              ...body,
              status: v.status,
            })
            .then(() => adminApi.post(`/admin/datasets/${editing.id}/refresh-count`))
        : adminApi.post('/admin/datasets', body)
      req
        .then(() => {
          message.success(editing ? '已保存' : '数据集已创建')
          closeModal()
          load()
        })
        .catch((e) => message.error(formatApiError(e, editing ? '保存失败' : '创建失败')))
        .finally(() => setSaving(false))
    })
  }

  const deleteRow = (row: DatasetRow) => {
    setDeletingId(row.id)
    adminApi
      .delete(`/admin/datasets/${row.id}`)
      .then(() => {
        message.success('已删除')
        load()
      })
      .catch((e) => message.error(formatApiError(e, '删除失败')))
      .finally(() => setDeletingId(null))
  }

  const exportJsonl = (row: DatasetRow) => {
    adminApi
      .get(`/admin/datasets/${row.id}/export.jsonl`, { responseType: 'blob' })
      .then((r) => {
        const blob = new Blob([r.data as BlobPart], { type: 'application/x-ndjson' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `${row.dataset_code || 'dataset'}.jsonl`
        a.click()
        URL.revokeObjectURL(url)
      })
      .catch((e) => message.error(formatApiError(e, '导出失败')))
  }

  const renderFilterCell = (filter: Record<string, unknown> | null | undefined) => {
    const f = filter || {}
    const dimId = f.dimension_id != null ? Number(f.dimension_id) : null
    const folderIds = Array.isArray(f.folder_ids) ? f.folder_ids : []
    const quality = (f.min_quality as string) || ''
    return {
      dimId,
      folderCount: folderIds.length,
      quality,
    }
  }

  return (
    <div>
      <PageHeader
        title="数据集管理"
        description="按虚拟分类与文件夹组合文档，发布后可供给前台检索、对话与第三方 Open API。"
      />
      <Card>
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginBottom: 16,
          }}
        >
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            新建数据集
          </Button>
        </div>
        <Table<DatasetRow>
          rowKey="id"
          loading={loading}
          dataSource={rows}
          scroll={{ x: 1140 }}
          pagination={tablePagination(page, total, handlePaginationChange, paginationOptions)}
          columns={[
            { title: '名称', dataIndex: 'name', width: 140, ellipsis: true },
            {
              title: '说明',
              dataIndex: 'description',
              width: 160,
              ellipsis: true,
              render: (v: string) => v || '—',
            },
            {
              title: '知识库',
              dataIndex: 'scope',
              width: 88,
              render: (s: string) => SCOPE_LABEL[s] || s,
            },
            {
              title: '虚拟分类',
              width: 120,
              ellipsis: true,
              render: (_, r) => {
                const { dimId } = renderFilterCell(r.filter_json)
                if (!dimId) return '—'
                return dimensionLabels.get(dimId) || `#${dimId}`
              },
            },
            {
              title: '文件夹',
              width: 88,
              render: (_, r) => {
                const { folderCount } = renderFilterCell(r.filter_json)
                return folderCount ? `${folderCount} 个` : '—'
              },
            },
            {
              title: '最低质量',
              width: 120,
              render: (_, r) => {
                const { quality } = renderFilterCell(r.filter_json)
                return quality ? QUALITY_LABEL[quality] || quality : '不限'
              },
            },
            {
              title: '状态',
              dataIndex: 'status',
              width: 72,
              render: (s: string) => (
                <Tag color={s === 'published' ? 'green' : 'default'}>
                  {s === 'published' ? '已发布' : '草稿'}
                </Tag>
              ),
            },
            { title: '文档数', dataIndex: 'item_count', width: 72 },
            {
              title: '公开',
              dataIndex: 'is_public',
              width: 56,
              render: (v: boolean) => (v ? '是' : '否'),
            },
            {
              title: '操作',
              width: 260,
              fixed: 'right',
              render: (_, r) => (
                <Space size="small" wrap>
                  <Button size="small" onClick={() => openItems(r)}>
                    查看文档集
                  </Button>
                  <Button size="small" onClick={() => openEdit(r)}>
                    编辑
                  </Button>
                  <Button size="small" onClick={() => exportJsonl(r)}>
                    导出
                  </Button>
                  <Popconfirm title={`确定删除「${r.name}」？`} onConfirm={() => deleteRow(r)}>
                    <Button size="small" danger loading={deletingId === r.id}>
                      删除
                    </Button>
                  </Popconfirm>
                </Space>
              ),
            },
          ]}
        />
      </Card>
      <Modal
        title={editing ? '编辑数据集' : '新建数据集'}
        open={modalOpen}
        onCancel={closeModal}
        onOk={submit}
        okText={editing ? '保存' : '创建'}
        cancelText="取消"
        confirmLoading={saving}
        destroyOnHidden
        width={580}
        styles={{ body: { paddingTop: 12 } }}
      >
        <Form
          form={form}
          layout="horizontal"
          requiredMark={false}
          labelCol={{ flex: '88px' }}
          wrapperCol={{ flex: 1 }}
          labelAlign="left"
          colon={false}
        >
          <DatasetRuleFields
            form={form}
            showPublicSwitch={!!editing}
            showStatus={!!editing}
          />
        </Form>
      </Modal>
      <Modal
        title={itemsDataset ? `文档集 · ${itemsDataset.name}` : '文档集'}
        open={!!itemsDataset}
        onCancel={closeItems}
        footer={null}
        width={760}
        destroyOnHidden
      >
        <Table<DatasetItemRow>
          rowKey="id"
          size="small"
          loading={itemsLoading}
          dataSource={itemRows}
          pagination={tablePagination(
            itemPage,
            itemTotal,
            handleItemPaginationChange,
            itemPaginationOptions,
          )}
          locale={{
            emptyText: itemsDataset?.item_count
              ? '规则已变更或文档已不满足筛选，请编辑数据集后保存并刷新文档数'
              : '暂无匹配文档，请确认数据集知识库、文件夹与最低质量设置',
          }}
          columns={[
            { title: '标题', dataIndex: 'title', ellipsis: true },
            {
              title: '类型',
              dataIndex: 'category',
              width: 120,
              ellipsis: true,
              render: (v: string) => v || '—',
            },
            {
              title: '索引状态',
              dataIndex: 'index_status',
              width: 96,
              render: (s: string) => (
                <Tag color={s === 'indexed' ? 'green' : s === 'failed' ? 'red' : 'default'}>
                  {INDEX_STATUS_LABEL[s] || s || '—'}
                </Tag>
              ),
            },
            {
              title: '质量',
              dataIndex: 'quality_tier',
              width: 72,
              render: (s: string) => ITEM_QUALITY_LABEL[s] || s || '未评',
            },
          ]}
        />
      </Modal>
    </div>
  )
}
